import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { Licence } from '@classes/licence/licence';
import { UserRole } from '@classes/licence/user-role';
import { Language } from '@classes/language';
import { User } from '@classes/licence/user';
import { LicenceType } from '@classes/licence/licence-type';
import { UiService } from './ui.service';

interface DropdownsContent {
    roles: UserRole[];
    languages: Language[];
    licenceTypes: LicenceType[];
    bankers: User[];
}

interface UserResponse {
    user: User;
    emailSent: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LicenceService {
    licencesListChanged = new Subject<Licence[]>();
    selectedLicenceChanged = new Subject<Licence>();

    private baseUrl: string = environment.apiUrl + 'licences/';
    private selectedLicence: Licence;

    constructor(
        private http: HttpClient,
        private uiSrv: UiService
    ) { }

    getList(): Observable<Licence[]> {
        return this.http.get<Licence[]>(this.baseUrl)
        .pipe(
            tap(licencesList => {
                this.licencesListChanged.next([ ...licencesList ]);
            })
        );
    }

    get(code?: string, emptyCurrent: boolean = true): Observable<Licence> {
        if (emptyCurrent)
            this.selectedLicenceChanged.next(null);

        const url = this.baseUrl + (code || 'current');

        return this.http.get<Licence>(url)
        .pipe(
            tap(licenceDetails => {
                this.selectedLicence = licenceDetails;
                this.selectedLicenceChanged.next({ ...licenceDetails });
            })
        );
    }

    getSelectedLicence(): Licence {
        return this.selectedLicence;
    }

    getCurrentRoles(): Observable<UserRole[]> {
        return this.http.get<UserRole[]>(this.baseUrl + 'current/roles');
    }

    post(licence: Licence): Observable<Licence> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        return this.http.post<Licence>(this.baseUrl, licence)
        .pipe(
            tap(() => {
                this.getList().subscribe(() => {
                    this.uiSrv.routeLoadingStateChanged.next(false);
                });
            })
        );
    }

    put(licence: Licence): Observable<Licence> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        return this.http.put<Licence>(this.baseUrl, licence)
        .pipe(
            tap(licenceDetails => {
                this.selectedLicence = licenceDetails;
                this.uiSrv.routeLoadingStateChanged.next(false);
                this.selectedLicenceChanged.next({ ...licenceDetails });
            })
        );
    }

    getDropdownsContent(): Observable<DropdownsContent> {
        return this.http.get<DropdownsContent>(this.baseUrl + 'lists');
    }

    postUser(user: User): Observable<User> {
        return this.http.post<UserResponse>(this.baseUrl + `${this.selectedLicence.code}/users`, user)
        .pipe(
            map((response: UserResponse) => {
                this.selectedLicence.users.push(response.user);
                this.selectedLicence.roles.find(r => r.code === response.user.role.code).userQuota.value ++;
                this.selectedLicenceChanged.next({ ...this.selectedLicence });
                this.showEmailSentMsg(response.emailSent);
                return response.user;
            })
        );
    }

    putUser(user: User): Observable<User> {
        return this.http.put<UserResponse>(this.baseUrl + `${this.selectedLicence.code}/users`, user)
        .pipe(map((response: UserResponse) => {
            this.showEmailSentMsg(response.emailSent);
            return response.user;
        }));
    }

    postRole(role: UserRole): Observable<UserRole> {
        return this.http.post<UserRole>(this.baseUrl + `${this.selectedLicence.code}/roles`, role)
        .pipe(
            tap(addedRole => {
                this.selectedLicence.roles.push(addedRole);
                this.selectedLicenceChanged.next({ ...this.selectedLicence });
            })
        );
    }

    putRole(role: UserRole): Observable<UserRole> {
        return this.http.put<UserRole>(this.baseUrl + `${this.selectedLicence.code}/roles`, role)
        .pipe(
            tap(editedRole => {
                this.selectedLicence.roles = this.selectedLicence.roles.map(r => r.code === editedRole.code ? editedRole : r);
                this.selectedLicenceChanged.next({ ...this.selectedLicence });
            })
        );
    }

    deleteRole(code: string): Observable<void> {
        return this.http.delete<void>(this.baseUrl + `${this.selectedLicence.code}/roles/${code}`)
        .pipe(
            tap(() => {
                this.selectedLicence.roles = this.selectedLicence.roles.filter(r => r.code !== code);
                this.selectedLicenceChanged.next({ ...this.selectedLicence });
            })
        );
    }

    private showEmailSentMsg(isEmailSent: boolean): void {
        if(isEmailSent) {
            this.uiSrv.showSnackbar('users.editForm.emailSent.fullLabel', true, 5000);
        }
    }
}
